import { Component, EventEmitter, Input, OnInit, Output, inject, viewChild } from '@angular/core';
import { NgForm, FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FlexLayoutModule } from '@angular/flex-layout';
import { NgxDatatableModule } from '@swimlane/ngx-datatable';
import { ColumnMode, SortType } from '@swimlane/ngx-datatable';
import { Observable, of } from 'rxjs';
import { CalculationBasedType, ConsumptionValidationToolEnum, IProduct, IProductRuleDetailStrategy, IProductRuleStrategy, ProductRuleDetailStrategy, ProductRuleStrategy, PunchCardReceiptToolType } from 'src/app/api/api-campaigns.service.generated';
import { GUID } from 'src/app/common/utils/GuidUtils';
import { PunchCardCampaignService } from '../../punch-card-campaign-edit/punch-card-campaign-edit.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MultiLabelsComponent } from 'src/app/common/components/multi-labels/multi-labels.component';
import { ButtonComponent } from 'wecheer-common';

export interface IExtendedProductRuleStrategy extends IProductRuleStrategy {
  visible?: boolean | undefined;
  guid?: string;
}
@UntilDestroy()
@Component({
  selector: 'app-product-rules-edit',
  templateUrl: './product-rules-edit.component.html',
  styleUrls: ['./product-rules-edit.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    FlexLayoutModule,
    NgxDatatableModule,
    MultiLabelsComponent,
    ButtonComponent
  ]
})

export class ProductRulesEditComponent implements OnInit {
  private punchCardCampaignService = inject(PunchCardCampaignService);

  public readonly formProductRules = viewChild<NgForm>('formProductRules');
  readonly datatable = viewChild<any>('datatable');
  @Input() captureToolType: string;
  @Output() onProductRulesChanged: EventEmitter<any> = new EventEmitter();

  consumptionValidationToolEnum: ConsumptionValidationToolEnum;
  allowedValidationTools: ConsumptionValidationToolEnum[] = [
    ConsumptionValidationToolEnum.SecretCode,
    ConsumptionValidationToolEnum.OCR,
    ConsumptionValidationToolEnum.FiscalQR,
    ConsumptionValidationToolEnum.GLOIntegration,
    ConsumptionValidationToolEnum.External,
    ConsumptionValidationToolEnum.AdvertiserCampaign,
    ConsumptionValidationToolEnum.HandshakeQR
  ];

  matchAlgorithms: { id: string, text: string }[];
  ColumnMode = ColumnMode;
  SortType = SortType;
  productRules: IExtendedProductRuleStrategy[];
  get productList(): IProduct[] {
    return this.punchCardCampaignService.productList;
  }
  filteredProducts: Observable<IProductRuleDetailStrategy[]> = of([]);

  searchTerm: string;
  searchCount: number;
  toggle = true;

  oldCaptureToolType: string;
  private isSettingValue = false;


  async ngOnInit() {
    this.consumptionValidationToolEnum = this.getValidationTool(this.captureToolType);
    if (!this.productRules)
      this.productRules = [];
    this.getFilteredProducts();
    this.setEventListeners();
  }

  setEventListeners() {
    this.punchCardCampaignService.onProductListFinished
      .pipe(untilDestroyed(this))
      .subscribe(_data => {
        this.getFilteredProducts();
        this.filterProductRules();
      })

    this.punchCardCampaignService.captureToolTypeChanged
      .pipe(untilDestroyed(this))
      .subscribe(newCaptureToolType => {
        if (this.oldCaptureToolType) {
          if (this.productRules) {
            this.productRules.forEach(p => {
              p.products = [];
            });
          }
        }
        this.oldCaptureToolType = newCaptureToolType;
        this.consumptionValidationToolEnum = this.getValidationTool(newCaptureToolType);

        this.getFilteredProducts();
        // Check and remove if product does not exist in product management
        this.filterProductRules();
      });
  }

  getValidationTool(captureToolType) {
    switch (captureToolType) {
      case PunchCardReceiptToolType.OCR:
        return ConsumptionValidationToolEnum.OCR;
      case PunchCardReceiptToolType.FiscalQRSephaz:
        return ConsumptionValidationToolEnum.FiscalQR;
      case PunchCardReceiptToolType.SecretCode:
        return ConsumptionValidationToolEnum.SecretCode;
      case PunchCardReceiptToolType.UniqueQRCode:
        return ConsumptionValidationToolEnum.UniqueQR;
      case PunchCardReceiptToolType.GLOIntegration:
        return ConsumptionValidationToolEnum.GLOIntegration;
      case PunchCardReceiptToolType.External:
        return ConsumptionValidationToolEnum.External;
      case PunchCardReceiptToolType.AdvertiserCampaign:
        return ConsumptionValidationToolEnum.AdvertiserCampaign;
      case PunchCardReceiptToolType.HandshakeQR:
        return ConsumptionValidationToolEnum.HandshakeQR;
    }
  }

  displayFn(selectedoption: any) {
    return selectedoption ? selectedoption.name : undefined;
  }

  getFilteredProducts() {
    const options = this.productList.filter(x => x.skus?.some(s => s.consumptionValidationTools?.some(t => t.validationToolEnum == this.consumptionValidationToolEnum)));
    let productDetails = [];
    options.forEach(x => {
      if (!this.productRules.some(r => r.products?.some(p => p.productId == x.id))) {
        let productRuleDetail: IProductRuleDetailStrategy = {
          productId: x.id,
          productName: x.name
        };
        productDetails.push(productRuleDetail);
      }
    });
    this.filteredProducts = of(productDetails);
  }

  getScoreTitle() {
    if (!this.punchCardCampaignService.baseCalculation || this.punchCardCampaignService.baseCalculation == CalculationBasedType.QuantityBased) {
      return "Score per product";
    }

    if (!this.punchCardCampaignService.currencyCode || this.punchCardCampaignService.currencyCode == "All Currencies") {
      return "Score per currency unit";
    }
    return `Score per ${this.punchCardCampaignService.currencyCode}`;
  }

  generateNewId(): string {
    return GUID.newGuid().replace(/-/g, '');
  }

  async setFormValue(filters: ProductRuleStrategy[]) {
    this.isSettingValue = true;
    try {
      // Add empty consumption filter item
      if (!filters || filters.length === 0) {
        return;
      }

      // Generate fake id
      let extendedFilters: IExtendedProductRuleStrategy[] = filters.map(f => ({
        ...f,
        guid: this.generateNewId(),
        visible: true
      }));

      this.productRules = extendedFilters;

      // Wait for Angular digest cycle
      await new Promise(resolve => setTimeout(resolve, 0));

      this.filterProductRules();
      this.getFilteredProducts();

      // Trigger change detection
      this.productRules = [...this.productRules];

    } finally {
      setTimeout(() => {
        this.isSettingValue = false;
      }, 100);
    }
  }
  getFormValue(): ProductRuleStrategy[] {
    const productRuleStrategies = this.productRules.map(cf => {
      let productRuleStrategy = new ProductRuleStrategy(cf);
      productRuleStrategy.products = cf.products?.map(p => {
        return new ProductRuleDetailStrategy(p);
      });
      return productRuleStrategy;
    });
    return productRuleStrategies;
  }

  productRulesInvalid(): Observable<boolean> {
    const invalidConditions = {
      noRules: !this.productRules || this.productRules.length === 0,
      invalidRules: this.productRules?.some(x =>
        !x.products ||
        x.products.length === 0 ||
        !x.score ||
        x.score === 0
      )
    };

    let res = of(false);
    if (invalidConditions.noRules || invalidConditions.invalidRules) {
      res = of(true);
    }

    if (invalidConditions.noRules && this.captureToolType === PunchCardReceiptToolType.HandshakeQR) {
      res = of(false);
    }

    return res;
  }

  isValid() {
    const formProductRules = this.formProductRules();
    if (!formProductRules)
      return true;

    this.resetSearch();

    let isValid = formProductRules.valid;
    isValid = this.productRules && this.productRules.length > 0 && !this.productRules.some(x => !x.products || x.products.length == 0 || !x.score || x.score == 0);
    if (!isValid)
      this.forceValidatorsToDisplayErrors();

    return isValid;
  }

  forceValidatorsToDisplayErrors() {
    // Force validators to display errors
    this.formProductRules().control.markAsTouched();
    this.formProductRules().control.markAsDirty();

    Object.keys(this.formProductRules().control.controls).forEach(key => {
      this.formProductRules().control.get(key).markAsTouched();
      this.formProductRules().control.get(key).markAsDirty();
    });
  }
  addProductRule() {
    let extendedProduct: IExtendedProductRuleStrategy = {
      guid: this.generateNewId(),
      products: [],
      score: null
    };
    this.productRules.push(extendedProduct);
    this.applySearch();
    this.productRules = [...this.productRules];
  }

  removeProductRule(guid: string) {
    const productRuleToRemove = this.productRules.find(cf => cf.guid === guid);
    const productRuleToRemoveIndex = this.productRules.indexOf(productRuleToRemove);
    this.productRules.splice(productRuleToRemoveIndex, 1);
    this.applySearch();
    this.productRules = [...this.productRules];
    this.getFilteredProducts();
  }

  onProductsChanged(_event: any) {
    this.getFilteredProducts();
    if (!this.isSettingValue) {
      this.onProductRulesChanged.emit(this.getFormValue());
    }
  }

  searchProductRule() {
    this.applySearch();
    this.productRules = [...this.productRules];
  }

  applySearch() {
    this.productRules.forEach(cf => {
      if (this.searchTerm) {
        if (cf.products) {
          cf.visible = cf.products.some(p => p.productName.toLowerCase().includes(this.searchTerm.toLowerCase()));
        }
        else {
          cf.visible = false;
        }
      }
      else
        cf.visible = true;
    });
    this.searchCount = this.searchTerm ? this.productRules.filter(cf => cf.visible).length : undefined;
  }

  async resetSearch() {
    this.searchTerm = undefined;
    this.applySearch();
    this.productRules = [...this.productRules];
  }

  onSort() {
    this.toggle = false;
    setTimeout(() => {
      this.toggle = true;
    });
  }

  onInput(ev: any, row: any, prop: string) {
    row[prop] = ev.target.value;
    if (!this.isSettingValue) {
      this.onProductRulesChanged.emit(this.getFormValue());
    }
  }

  private filterProductRules() {
    if (!this.productList.length) {
      return;
    }
    this.productRules.forEach(rule => {
      rule.products = rule.products?.filter((pro) => {
        const hasPro = this.productList.some(proItm => proItm.id === pro.productId);
        return hasPro;
      });
    });
  }
}

